import { html, LitElement, TemplateResult, PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import type { GridCellType } from '../types';
import { ColumnType } from '../../../types/common';
import { EnumListItem } from '../../../../ui/gzEnumList/types';
import {
  WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD,
  WEB_COMPONENT_GZ_ENUM_FILTER,
  WEB_COMPONENT_GZ_FORM_FIELD,
} from '../../../../webcomponents.constants';
import { GzEnumFilterWebComponent } from '../../../../ui/gzEnumFilter';
import { CellEditEvents } from '../types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormFieldInput } from '../../../../ui/gzForm/formField/component/FormFieldInput';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzCopyToClipboardWebComponent } from '../../../../ui/gzCopyToClipboard';
import { translateFn } from '../../../../utils/translator.helper';

export class GridCellEditWebComponent extends LitElement {
  /**
   * When set to TRUE, the editing field is displayed or enabled
   */
  @property({ type: Boolean, reflect: true })
  active = false;

  /**
   * This property is programmatically set to TRUE when the grid row containing this renderer is selected.
   * The row selected state is available on the GridItem data model.
   */
  @property({ type: Boolean, reflect: true })
  selected = false;

  /**
   * This property will reflect the string | number | boolean cell value, either from the input value, or the enum list
   * checked options
   */
  @property({ reflect: true })
  value;

  /**
   * Use this property to determine what kind of edit field we need to display in edit mode.
   * We only support string, number, enum, select and checkbox types at the moment
   */
  @property({ type: Number, reflect: true })
  type: GridCellType;

  /**
   * The options list for ENUM column type
   */
  @property({ type: Array })
  filterOptions?: EnumListItem[] = [];

  /**
   * The string for the ENUM component confirm button
   */
  @property({ type: String })
  confirmActionButtonString? = '';

  /**
   * The string for the ENUM component 'ALL OPTIONS SELECTED'
   */
  @property({ type: String, attribute: true })
  allItemsLabel? = '';

  @property({ type: Boolean, reflect: true })
  canSelectAll = false;

  @property({ type: Boolean, reflect: true })
  allowSelectNone = false;

  /**
   * If true, a copyToClipboard button will be displayed on mouseenter
   */
  @property({ type: Boolean, reflect: true })
  canCopyContent = false;

  /**
   * This message will be displayed in a tooltip when the `copy to clipboard` button is clicked
   */
  @property({ type: String, reflect: true })
  copyContentMessage;

  /**
   * If set, provides a custom message to be copied to clipboard instead of the cell inner text
   */
  @property({ type: String, reflect: true })
  clipboardMessage: string;

  /**
   * If set to true, the select dropdown will go into dynamic search mode
   */
  @property({ type: Boolean, reflect: true })
  selectWithDynamicSearch = false;

  @query(WEB_COMPONENT_GZ_ENUM_FILTER)
  private readonly enumField!: GzEnumFilterWebComponent;

  @query('[gz-form-input]')
  inputField!: FormFieldInput<string | number | boolean>;

  @query(WEB_COMPONENT_GZ_FORM_FIELD)
  public formField: GzFormFieldWebComponent;

  @query(WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD)
  private copyContentBtn!: GzCopyToClipboardWebComponent;

  private cachedEnumSelectedValues: string | null;

  /**
   * This internal property is used for displaying a placeholder in the label when value is empty, such as '-' for
   * strings or '0' for numbers
   * @private
   */
  private displayedValue: number | string;

  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('active')) {
      this.matchCellEditValueToFilterOptions();

      if (this.active && this.inputField !== null) {
        this.inputField.setFormValue(this.value);
      }

      if (this.type === ColumnType.CHECKBOX) {
        this.inputField.disabled = !this.active;
      }
    }
  }

  render() {
    return html`${this.renderFieldBasedOnType()}`;
  }

  private renderEnumEditField(): TemplateResult {
    this.matchCellEditValueToFilterOptions();

    return html` <gz-grid-cell-default-renderer
        class="cell-edit-label"
        ?canCopyContent="${this.canCopyContent}"
        .label="${this.value}"
        .copyContentMessage="${this.copyContentMessage}"
        .clipboardMessage="${this.clipboardMessage}"
        ?selected="${this.selected}"
      >
      </gz-grid-cell-default-renderer>
      <gz-form-field class="form-field-enum">
        <gz-enum-filter
          gz-form-input
          labelWidth="full"
          .confirmActionButtonString="${this.confirmActionButtonString}"
          .allItemsLabel="${this.allItemsLabel}"
          .filterOptions="${this.filterOptions}"
          .canSelectAll="${this.canSelectAll}"
          .allowSelectNone="${this.allowSelectNone}"
          shouldAlignVertically
          class="cell-edit-field"
          @open="${this.handleFilterOpen}"
          @close="${this.handleFilterClose}"
          @selectionChange="${this.handleEnumSelectionConfirmed}"
        ></gz-enum-filter>
      </gz-form-field>`;
  }

  private renderSelectEditField(): TemplateResult {
    return html`
      <gz-grid-cell-default-renderer
        class="cell-edit-label"
        ?canCopyContent="${this.canCopyContent}"
        .label="${this.getSelectLabel()}"
        .copyContentMessage="${this.copyContentMessage}"
        .clipboardMessage="${this.clipboardMessage}"
        ?selected="${this.selected}"
      >
      </gz-grid-cell-default-renderer>
      <gz-form-field class="form-field-single-select">
        <gz-single-select
          gz-form-input
          cannotDelete
          shouldAlignVertically
          .value="${this.value}"
          .labelWidth="full"
          .filterOptions="${this.filterOptions}"
          ?hasDynamicSearch="${this.selectWithDynamicSearch}"
          .placeholder="${translateFn('Select option')}"
          .noSearchResultsString="${translateFn('No options found')}"
          .searchPlaceholderString="${translateFn('Search')}"
          class="cell-edit-field"
        ></gz-single-select>
      </gz-form-field>
    `;
  }

  private renderInputEditField(type: ColumnType): TemplateResult {
    let inputTemplate = html``;

    if (type === ColumnType.STRING) {
      this.displayedValue = isEmpty(this.value) ? '-' : this.value;
      inputTemplate = html`<gz-input gz-form-input .value="${this.value}" inputwidth="full"></gz-input>`;
    }

    if (type === ColumnType.NUMBER) {
      this.displayedValue = isEmpty(this.value) ? 0 : +this.value;
      inputTemplate = html`<gz-input-number gz-form-input .value="${+this.value}" inputwidth="108"></gz-input-number>`;
    }

    return html` <gz-grid-cell-default-renderer
        class="cell-edit-label"
        ?canCopyContent="${this.canCopyContent && !isEmpty(this.value)}"
        .label="${this.displayedValue}"
        .copyContentMessage="${this.copyContentMessage}"
        .clipboardMessage="${this.clipboardMessage}"
        ?selected="${this.selected}"
      >
      </gz-grid-cell-default-renderer>
      <gz-form-field required class="cell-edit-field">${inputTemplate}</gz-form-field>`;
  }

  private renderFieldBasedOnType(): TemplateResult {
    switch (this.type) {
      case ColumnType.ENUM:
        return this.renderEnumEditField();

      case ColumnType.EDITABLE_SELECT:
        return this.renderSelectEditField();

      case ColumnType.STRING:
      case ColumnType.NUMBER:
        return this.renderInputEditField(this.type);

      case ColumnType.CHECKBOX:
        return this.renderCheckboxEditField();
      default:
        const realValue = this.value ?? '-';

        return html`<div class="cell-default-label">${realValue}</div>`;
    }
  }

  private renderCheckboxEditField(): TemplateResult {
    const isChecked = isEmpty(this.value) ? false : this.value;
    return html` <gz-form-field class="checkbox-field" ?disabled="${!this.active}">
      <gz-checkbox gz-form-input ?checked="${isChecked}"></gz-checkbox>
    </gz-form-field>`;
  }

  public handleEditFieldChange() {
    switch (this.type) {
      case ColumnType.ENUM:
        this.value = this.enumField.filterOptions
          .filter(option => option.selected)
          .map(option => option.label)
          .join(', ');
        this.filterOptions = this.enumField.filterOptions;
        break;

      case ColumnType.STRING:
      case ColumnType.NUMBER:
      case ColumnType.CHECKBOX:
      case ColumnType.EDITABLE_SELECT:
        this.value = this.inputField.getFormValue();
        break;
    }
  }

  public discardCellEditChanges() {
    let realValue;

    switch (this.type) {
      case ColumnType.ENUM:
        if (this.enumField) {
          this.enumField.dropdownMenu.selectedValuesStrings = this.value;
        }
        break;

      case ColumnType.EDITABLE_SELECT:
        if (this.inputField) {
          this.inputField.setFormValue(this.value);
        }
        break;

      case ColumnType.STRING:
        if (this.inputField) {
          realValue = isEmpty(this.value) ? '' : this.value;
          this.inputField.setFormValue(realValue);
        }
        break;

      case ColumnType.NUMBER:
        if (this.inputField) {
          realValue = isEmpty(this.value) ? 0 : +this.value;
          this.inputField.setFormValue(realValue);
        }
        break;

      case ColumnType.CHECKBOX:
        if (this.inputField) {
          realValue = isEmpty(this.value) ? false : this.value;
          this.inputField.setFormValue(realValue);
        }
        break;
    }

    this.dispatchEvent(new CustomEvent(CellEditEvents.CANCEL));
  }

  public saveCellEditChanges() {
    this.handleEditFieldChange();
    this.dispatchEvent(
      new CustomEvent(CellEditEvents.SAVE, {
        detail: { value: this.value },
      })
    );
  }

  public handleFilterOpen() {
    this.dispatchEvent(new CustomEvent('open', { bubbles: true }));
  }

  public handleFilterClose() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
  }

  public closeEnumDropdown() {
    if (!this.enumField) {
      return;
    }

    if (this.enumField.dropdownMenu && this.enumField.dropdownMenu.open) {
      this.enumField.dropdownMenu.closeDropdownMenu();
    }
  }

  public matchCellEditValueToFilterOptions() {
    if (this.type === ColumnType.ENUM && this.filterOptions) {
      const selectedValuesString = this.cachedEnumSelectedValues ? this.cachedEnumSelectedValues : this.value;
      const labels = selectedValuesString.split(',').map(label => label.trim());
      this.filterOptions = this.filterOptions.map(item => {
        const isSelected = labels.includes(item.label);
        return { ...item, selected: isSelected };
      });
    }
  }

  private getSelectLabel(): string | undefined {
    if (!this.filterOptions) {
      return undefined;
    }
    const option = this.filterOptions.find(item => item.value === this.value);
    return option?.label;
  }

  private handleEnumSelectionConfirmed(event: CustomEvent) {
    if (!this.filterOptions) {
      return;
    }
    const { selectedValues } = event.detail;

    this.cachedEnumSelectedValues = this.filterOptions
      .filter(option => selectedValues.includes(option.value))
      .map(el => el.label)
      .join(', ');
  }

  public closeCellEditMode() {
    this.active = false;
    if (this.type === ColumnType.ENUM) {
      this.cachedEnumSelectedValues = null;
      this.enumField.dropdownMenu.closeDropdownMenu();
    }
  }
}
